import { Booking, AdminSettings } from '../data/types'

export interface EmailNotificationResult {
  sent: boolean
  message: string
}

const FALLBACK_EMAILJS = {
  publicKey: 'LOdNPaOrBU0cx_BUF',
  serviceId: 'service_qevwrir',
  templateId: 'template_ivtcy5w',
  notificationEmail: 'gregss70@gmail.com',
}

/** Sends an admin notification through EmailJS after a booking request. */
export const sendBookingEmail = async (
  booking: Booking,
  settings: AdminSettings,
): Promise<EmailNotificationResult> => {
  const configured = settings.emailJs
  const cfg = {
    publicKey: configured?.publicKey || FALLBACK_EMAILJS.publicKey,
    serviceId: configured?.serviceId || FALLBACK_EMAILJS.serviceId,
    templateId: configured?.templateId || FALLBACK_EMAILJS.templateId,
  }
  const recipient =
    configured?.notificationEmail ||
    settings.contactInfo.email ||
    FALLBACK_EMAILJS.notificationEmail

  try {
    const response = await fetch(
      'https://api.emailjs.com/api/v1.0/email/send',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id: cfg.serviceId,
          template_id: cfg.templateId,
          user_id: cfg.publicKey,
          template_params: {
            to_email: recipient,
            subject: 'Novi upit za termin — JumpLand',
            customer_name: booking.customerName,
            phone: booking.phone,
            email: booking.email || '-',
            event_address: booking.eventAddress,
            product_name: booking.productName,
            selected_variant: booking.selectedVariant || '-',
            date: booking.date,
            end_date: booking.endDate || '',
            start_time: booking.startTime,
            end_time: booking.endTime,
            include_delivery: booking.includeDelivery ? 'Da' : 'Ne (preuzimanje)',
            total_price: booking.totalPrice,
            note: booking.note || '-',
          },
        }),
      },
    )

    if (!response.ok) {
      const details = await response.text()
      console.error('[Email] EmailJS odgovor:', details)
      return {
        sent: false,
        message: details || `EmailJS je vratio grešku ${response.status}.`,
      }
    }

    return { sent: true, message: 'EmailJS je prihvatio obavijest.' }
  } catch (error) {
    console.error('[Email] Greška:', error)
    return {
      sent: false,
      message:
        error instanceof Error
          ? error.message
          : 'Nije moguće povezati se s EmailJS-om.',
    }
  }
}

/**
 * Builds a WhatsApp URL with a pre-filled booking message.
 * The customer clicks to open WhatsApp and send to the admin.
 */
export const buildWhatsAppBookingUrl = (
  booking: Booking,
  adminPhone: string,
): string => {
  const cleanPhone = adminPhone.replace(/\D/g, '')
  // Convert local Croatian phone (0xx) to international (+385xx)
  const intlPhone = cleanPhone.startsWith('0')
    ? '385' + cleanPhone.substring(1)
    : cleanPhone

  const message = [
    '🎉 *NOVA REZERVACIJA*',
    '',
    `👤 *Klijent:* ${booking.customerName}`,
    `📞 *Telefon:* ${booking.phone}`,
    `✉️ *Email:* ${booking.email}`,
    `📍 *Adresa:* ${booking.eventAddress}`,
    '',
    `🎈 *Proizvod:* ${booking.productName}`,
    booking.selectedVariant ? `🔸 *Opcija:* ${booking.selectedVariant}` : '',
    booking.endDate && booking.endDate !== booking.date
      ? `📅 *Termin:* ${booking.date} – ${booking.endDate}`
      : `📅 *Datum:* ${booking.date}`,
    `⏰ *Vrijeme:* ${booking.startTime} - ${booking.endTime}`,
    `🚚 *Dostava:* ${booking.includeDelivery ? 'Da' : 'Ne (preuzimanje)'}`,
    `💰 *Cijena:* ${booking.totalPrice} €`,
    booking.note ? `📝 *Napomena:* ${booking.note}` : '',
    booking.locationImage || booking.locationImagePath
      ? `📸 *Klijent je priložio sliku lokacije* (vidljiva u admin panelu)`
      : '',
  ]
    .filter(Boolean)
    .join('\n')

  return `https://wa.me/${intlPhone}?text=${encodeURIComponent(message)}`
}
